import type { ReactNode } from 'react';

export interface NavItem {
  to: string;
  label: string;
  /** Título mostrado en el header de la página. */
  titulo: string;
  icon: ReactNode;
  /** Marca visual de área aún no funcional. */
  enDesarrollo?: boolean;
  /** Ayuda contextual (ⓘ junto al título de la página, regla 2026-10-19). */
  ayuda?: string;
  /** No se pinta en la sidebar; solo da título y ayuda a su ruta (p. ej. Entrenamiento). */
  oculto?: boolean;
}

export interface NavGrupo {
  /** Label de sección de la sidebar (única mayúscula sancionada por la guía). */
  titulo: string;
  items: NavItem[];
}

const iconClass = 'h-4 w-4 shrink-0';
const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** Navegación del área privada (rol user), agrupada como en la guía visual. */
export const NAV_GRUPOS: NavGrupo[] = [
  {
    titulo: 'Operación',
    items: [
      {
        to: '/panel',
        label: 'Panel de Control',
        titulo: 'Panel de Control',
        ayuda: 'Tablero de gestión de AMBEC Plus: cartera de DIAs, pendientes de atención, actividad, costos y un resumen del Newsletter.',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <rect x="3" y="3" width="7" height="9" rx="1.5" />
            <rect x="14" y="3" width="7" height="5" rx="1.5" />
            <rect x="14" y="12" width="7" height="9" rx="1.5" />
            <rect x="3" y="16" width="7" height="5" rx="1.5" />
          </svg>
        ),
      },
      {
        to: '/newsletter',
        label: 'Newsletter',
        titulo: 'Newsletter',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        ),
      },
      {
        to: '/clientes',
        label: 'Gestión de Clientes',
        titulo: 'Gestión de Clientes',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        ),
      },
    ],
  },
  {
    titulo: 'AMBEC',
    items: [
      {
        to: '/sharepoint',
        label: 'SharePoint',
        titulo: 'AMBEC SharePoint',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <path d="M8 13h8M8 16h5" />
          </svg>
        ),
      },
    ],
  },
  {
    titulo: 'Agentes',
    items: [
      {
        to: '/agentes/prefactibilidad',
        label: 'Prefactibilidad',
        titulo: 'Prefactibilidad',
        enDesarrollo: true,
        icon: (
          <svg {...svgProps} className={iconClass}>
            <circle cx="12" cy="12" r="9" />
            <path d="m15.5 8.5-2 5-5 2 2-5z" />
          </svg>
        ),
      },
      {
        to: '/agentes/dia',
        label: 'Agente DIA',
        titulo: 'DIA — Declaración de Impacto Ambiental',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
            <path d="M14 3v6h6" />
            <path d="M9 16c0-2.5 1.8-4 4.5-4-.3 2.8-1.8 4.4-4.5 4z" />
          </svg>
        ),
      },
      {
        to: '/agentes/dia/entrenar',
        label: 'Entrenamiento',
        titulo: 'Entrenamiento de Andrés',
        oculto: true,
        icon: null,
        ayuda: 'Lo que Andrés sabe para TODAS las DIA: su Biblioteca, los casos de referencia (de AMBEC y del e-SEIA) y su oficio. Nada de lo que hagas aquí toca un proyecto en curso.',
      },
      {
        to: '/agentes/investigador-seia',
        label: 'Agente eSEIA',
        titulo: 'Agente eSEIA — Elena',
        ayuda: 'Elena consulta en vivo el portal público del Servicio de Evaluación Ambiental (e-SEIA): todos los proyectos evaluados en Chile, DIA y EIA, con sus expedientes. Sirve para catastros, fichas y lecciones aprendidas de proyectos de terceros.',
        icon: (
          <svg {...svgProps} className={iconClass}>
            <circle cx="10.5" cy="10.5" r="6.5" />
            <path d="m20 20-4.8-4.8" />
            <path d="M8 10.5h5M10.5 8v5" />
          </svg>
        ),
      },
      {
        to: '/agentes/eia',
        label: 'EIA',
        titulo: 'EIA — Estudio de Impacto Ambiental',
        enDesarrollo: true,
        icon: (
          <svg {...svgProps} className={iconClass}>
            <path d="M3 19h18" />
            <path d="m5 19 5-9 3.5 6L17 9l4 10" />
            <circle cx="7.5" cy="6.5" r="1.8" />
          </svg>
        ),
      },
    ],
  },
];

/** Lista plana (título de página actual, rutas, etc.). */
export const NAV_ITEMS: NavItem[] = NAV_GRUPOS.flatMap((g) => g.items);
